import { useState, useCallback, useEffect, useRef } from 'react';
import { VonChatFilters, DEFAULT_FILTERS } from './types';
import { selectOptimalModel, getActualModelName } from './modelConfigs';
import { vonRateLimiter } from './rateLimiter';

interface VonSession {
  id: string;
  title: string;
  sessionType: string;
  totalMessages: number;
  lastActivityAt: string;
  createdAt: string;
  isArchived: boolean;
}

interface VonMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  modelUsed?: string;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  estimatedCostUsd?: number;
  createdAt: string;
}

export const useVonChat = () => {
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState<VonChatFilters>(DEFAULT_FILTERS);
  const [messages, setMessages] = useState<VonMessage[]>([]);
  const [sessions, setSessions] = useState<VonSession[]>([]);
  const [currentSession, setCurrentSession] = useState<VonSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Generate session title based on current time
  const generateSessionTitle = useCallback((): string => {
    const now = new Date();
    const time = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const date = now.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    return `Von Chat - ${date}, ${time}`;
  }, []);

  // Load sessions from API
  const loadSessions = useCallback(async () => {
    try {
      const response = await fetch('/api/ai-chat/sessions', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to load sessions: ${response.statusText}`);
      }

      const data = await response.json() as { sessions: VonSession[] };
      const vonSessions = (data.sessions || []).filter((session: VonSession) => 
        session.sessionType === 'von_chat'
      );
      
      setSessions(vonSessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      setError('Failed to load chat sessions');
    }
  }, []);

  // Create new session
  const createNewSession = useCallback(async () => {
    try {
      setIsCreatingSession(true);
      setError(null);

      // Rate limiting check
      const rateLimitCheck = await vonRateLimiter.checkRateLimit();
      if (!rateLimitCheck.allowed) {
        throw new Error(`Please wait ${Math.ceil((rateLimitCheck.retryAfter || 1000) / 1000)} seconds before creating a new session.`);
      }

      const response = await fetch('/api/ai-chat/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          title: generateSessionTitle(),
          sessionType: 'von_chat' // Distinguish Von Chat sessions
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      const result = await response.json() as { session: VonSession };
      const newSession = result.session;

      // Update local state
      setCurrentSession(newSession);
      setSessions(prev => [newSession, ...prev]);
      setMessages([]);
      setPagination(null);
      setIsCreatingSession(false);

      return newSession;
    } catch (error: any) {
      console.error('Failed to create session:', error);
      setError(error.message);
      setIsCreatingSession(false);
      throw error;
    }
  }, [generateSessionTitle]);

  // Select and load a session with pagination
  const selectSession = useCallback(async (sessionId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Find session in local state first
      const session = sessions.find(s => s.id === sessionId);
      if (session) {
        setCurrentSession(session);
      }

      // Load session details and recent messages (10 most recent)
      const response = await fetch(`/api/ai-chat/sessions?sessionId=${sessionId}&limit=10&offset=0`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to load session: ${response.statusText}`);
      }

      const data = await response.json() as { 
        session?: VonSession; 
        messages?: VonMessage[];
        pagination?: {
          total: number;
          limit: number;
          offset: number;
          hasMore: boolean;
        };
      };
      
      if (data.session) {
        setCurrentSession(data.session);
        setMessages(data.messages || []);
        setPagination(data.pagination || null);
      }

      setIsLoading(false);
    } catch (error: any) {
      console.error('Failed to select session:', error);
      setError(error.message);
      setIsLoading(false);
    }
  }, [sessions]);

  // Send message to AI
  const sendMessage = useCallback(async () => {
    if (!message.trim() || isLoading) return;

    // Create session if none exists
    let activeSession = currentSession;
    if (!activeSession) {
      try {
        activeSession = await createNewSession();
      } catch (error) {
        return; // Error already handled in createNewSession
      }
    }

    // Check message limit (100 messages max)
    const MESSAGE_LIMIT = 100;
    const currentMessageCount = pagination?.total || activeSession.totalMessages;
    
    if (currentMessageCount >= MESSAGE_LIMIT) {
      setError(`This conversation has reached the maximum limit of ${MESSAGE_LIMIT} messages. Please start a new chat to continue.`);
      return;
    }
    
    // Show warning when approaching limit
    if (currentMessageCount >= MESSAGE_LIMIT - 10) {
      const remaining = MESSAGE_LIMIT - currentMessageCount;
      console.warn(`Von Chat: Approaching message limit. ${remaining} messages remaining before limit of ${MESSAGE_LIMIT}.`);
    }

    const userMessageContent = message.trim();
    setMessage('');
    setIsLoading(true);
    setError(null);

    // Add user message immediately to UI
    const tempUserMessage: VonMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: userMessageContent,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // Rate limiting check
      const rateLimitCheck = await vonRateLimiter.checkRateLimit();
      if (!rateLimitCheck.allowed) {
        throw new Error(`Please wait ${Math.ceil((rateLimitCheck.retryAfter || 1000) / 1000)} seconds before sending another message.`);
      }

      // Auto-select model if none selected
      const selectedModel = filters.selectedModel || selectOptimalModel(
        userMessageContent, 
        !!(filters.dateRange || filters.selectedClients?.length)
      );

      // Prepare enhanced filtering parameters
      const searchFilters: any = {};
      
      // Add date range filtering
      if (filters.dateRange) {
        searchFilters.dateRange = {
          startDate: filters.dateRange.startDate,
          endDate: filters.dateRange.endDate
        };
      }
      
      // Add client filtering
      if (filters.selectedClients && filters.selectedClients.length > 0) {
        searchFilters.clientIds = filters.selectedClients;
      }

      // Prepare request for AI chat API with enhanced filters
      const requestData = {
        sessionId: activeSession!.id,
        message: userMessageContent,
        forceModel: getActualModelName(selectedModel),
        useClassification: filters.useNeuralSearch,
        precisionSearchQuery: filters.precisionSearchQuery, // Add precision search query
        maxContextChunks: 5,
        rawChatMode: filters.rawChatMode, // Add Raw Chat Mode parameter
        searchFilters: Object.keys(searchFilters).length > 0 ? searchFilters : undefined,
        vonChatFilters: filters // Send complete filter state for backend processing
      };

      console.log('Von Chat: Sending message with model:', selectedModel, '→', getActualModelName(selectedModel));

      // Send to AI chat API with extended timeout for complex queries
      const controller = abortControllerRef.current;
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 120000); // Increase to 120 seconds to match backend timeout

      const response = await fetch('/api/ai-chat/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify(requestData)
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || `API Error: ${response.statusText}`);
      }

      const result = await response.json() as {
        messageId?: string;
        response: string;
        metadata?: {
          modelUsed?: string;
          usage?: {
            prompt_tokens?: number;
            completion_tokens?: number;
            total_tokens?: number;
          };
          estimatedCostUsd?: number;
          processingTimeMs?: number;
        };
      };

      // Create assistant message
      const assistantMessage: VonMessage = {
        id: result.messageId || `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.response,
        modelUsed: result.metadata?.modelUsed,
        tokens: result.metadata?.usage ? {
          prompt: result.metadata.usage.prompt_tokens || 0,
          completion: result.metadata.usage.completion_tokens || 0,
          total: result.metadata.usage.total_tokens || 0
        } : undefined,
        estimatedCostUsd: result.metadata?.estimatedCostUsd,
        createdAt: new Date().toISOString()
      };

      // Update messages (replace temp user message with real ones)
      setMessages(prev => {
        // Remove the temporary user message and add both real messages
        const withoutTemp = prev.filter(msg => msg.id !== tempUserMessage.id);
        return [...withoutTemp, 
          {
            ...tempUserMessage,
            id: `user-${Date.now()}` // Give it a proper ID
          },
          assistantMessage
        ];
      });

      // Update session message count
      if (activeSession) {
        setCurrentSession(prev => prev ? {
          ...prev,
          totalMessages: prev.totalMessages + 2,
          lastActivityAt: new Date().toISOString()
        } : null);
      }

      console.log('Von Chat: Response received from', result.metadata?.modelUsed, 
                  'in', result.metadata?.processingTimeMs, 'ms');

    } catch (error: any) {
      console.error('Von Chat: Send message error:', error);
      
      // Check if this is actually a successful response that got aborted
      if (error.name === 'AbortError') {
        console.log('Von Chat: Request was aborted - checking if response was successful');
        // Don't immediately show error for abort - the response might have succeeded
        // Let the user try again or refresh to see if the message was actually sent
        setError('Request was interrupted. Please refresh the page if your message doesn\'t appear.');
        setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
        setIsLoading(false);
        return;
      }
      
      // Handle different error types with user-friendly messages
      let errorMessage = 'An error occurred while processing your request.';
      
      if (error.message.includes('timeout') || error.message.includes('timed out')) {
        errorMessage = 'Request timed out. Try using a faster model (A-series) or simplify your query.';
      } else if (error.message.includes('rate limit')) {
        errorMessage = error.message; // Use the specific rate limit message
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
      // Always remove the temporary user message on error and ensure UI stays responsive
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
      
      // Force UI update to prevent blank screen
      setTimeout(() => {
        setIsLoading(false);
        console.log('Von Chat: UI state reset after error');
      }, 100);
    } finally {
      // Ensure loading state is always cleared
      setIsLoading(false);
    }
  }, [message, isLoading, currentSession, filters, createNewSession]);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<VonChatFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Update session title
  const updateSessionTitle = useCallback(async (sessionId: string, newTitle: string) => {
    try {
      const response = await fetch(`/api/ai-chat/sessions?sessionId=${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ title: newTitle })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || `Failed to update session title: ${response.statusText}`);
      }

      // Update local state
      setSessions(prev => prev.map(session => 
        session.id === sessionId 
          ? { ...session, title: newTitle }
          : session
      ));

      // Update current session if it's the one being edited
      if (currentSession?.id === sessionId) {
        setCurrentSession(prev => prev ? { ...prev, title: newTitle } : null);
      }

      return true;
    } catch (error: any) {
      console.error('Failed to update session title:', error);
      setError(error.message);
      return false;
    }
  }, [currentSession]);

  // Delete session
  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      const response = await fetch(`/api/ai-chat/sessions?sessionId=${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || `Failed to delete session: ${response.statusText}`);
      }

      // Update local state
      setSessions(prev => prev.filter(session => session.id !== sessionId));

      // If we deleted the current session, clear it
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
      }

      return true;
    } catch (error: any) {
      console.error('Failed to delete session:', error);
      setError(error.message);
      return false;
    }
  }, [currentSession]);

  // Load more messages
  const loadMoreMessages = useCallback(async () => {
    if (!currentSession || !pagination || !pagination.hasMore || isLoadingMore) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError(null);

      // Calculate next offset
      const nextOffset = pagination.offset + pagination.limit;

      // Load older messages
      const response = await fetch(
        `/api/ai-chat/sessions?sessionId=${currentSession.id}&limit=${pagination.limit}&offset=${nextOffset}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load more messages: ${response.statusText}`);
      }

      const data = await response.json() as { 
        messages?: VonMessage[];
        pagination?: {
          total: number;
          limit: number;
          offset: number;
          hasMore: boolean;
        };
      };

      if (data.messages) {
        // Prepend older messages to the beginning of the array
        // Note: Scroll position preservation will be handled in the UI component
        setMessages(prev => [...(data.messages || []), ...prev]);
        setPagination(data.pagination || null);
      }

      setIsLoadingMore(false);
    } catch (error: any) {
      console.error('Failed to load more messages:', error);
      setError(error.message);
      setIsLoadingMore(false);
    }
  }, [currentSession, pagination, isLoadingMore]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    message,
    filters,
    messages,
    sessions,
    currentSession,
    isLoading,
    isCreatingSession,
    error,
    pagination,
    isLoadingMore,
    
    // Actions
    setMessage,
    updateFilters,
    clearFilters,
    sendMessage,
    createNewSession,
    selectSession,
    loadSessions,
    clearError,
    updateSessionTitle,
    deleteSession,
    loadMoreMessages
  };
}; 
